/**
 * Marketing Terminal — Creative Studio
 * Master spec §2.4. Templates + asset library + AI generation request.
 */

import React, { useState } from 'react';
import { ImageIcon, Layers, Sparkles, Library, Video, Mic, User, AlertTriangle, Check, Wand2 } from 'lucide-react';
import { QueryClient, QueryClientProvider, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '../../../utils/adminApi';

interface Asset {
  id: string;
  name: string;
  asset_kind: string;
  rendered_url: string;
  generation_tool: string | null;
  generation_cost_cents: number;
  created_at: string;
  persona_tags: string[];
  channel_tags: string[];
}

interface GenerationJob {
  id: string;
  tool: string;
  asset_type: string;
  prompt: string;
  status: string;
  estimated_cost_cents: number;
  actual_cost_cents: number | null;
  output_url: string | null;
  cost_approval_required: boolean;
  error_code: string | null;
  error_message: string | null;
  requested_at: string;
}

const queryClient = new QueryClient({ defaultOptions: { queries: { staleTime: 30_000 } } });

export function CreativeStudioPage() {
  return (
    <QueryClientProvider client={queryClient}>
      <Inner />
    </QueryClientProvider>
  );
}

function Inner() {
  const [tab, setTab] = useState<'library' | 'generate' | 'jobs'>('generate');

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: "'League Spartan', sans-serif", color: 'var(--cedar-ink)' }}>
          Creative Studio
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Image, video, voiceover, presenter generation. Cheapest-first routing.
          Pre-generation cost preview + approval gate above $5.
        </p>
      </div>

      <div className="flex gap-2 border-b border-gray-200">
        {[
          { key: 'generate', label: 'Generate', icon: Sparkles },
          { key: 'library', label: 'Asset Library', icon: Library },
          { key: 'jobs', label: 'Job Queue', icon: Layers },
        ].map(t => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key as 'library' | 'generate' | 'jobs')}
              className="flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium"
              style={active ? { borderColor: 'var(--cedar-primary)', color: 'var(--cedar-primary)' } : { borderColor: 'transparent', color: 'var(--text-secondary)' }}
            >
              <Icon className="h-4 w-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'generate' && <GenerateTab />}
      {tab === 'library' && <LibraryTab />}
      {tab === 'jobs' && <JobsTab />}
    </div>
  );
}

function GenerateTab() {
  const queryClient = useQueryClient();
  const [assetType, setAssetType] = useState<'image' | 'video' | 'voiceover' | 'presenter'>('image');
  const [prompt, setPrompt] = useState('');
  const [tool, setTool] = useState('');
  const [qualityTier, setQualityTier] = useState<'default' | 'premium'>('default');
  const [needsText, setNeedsText] = useState(false);

  const generate = useMutation({
    mutationFn: () =>
      adminFetch('/api/admin/marketing/creative/generate', {
        method: 'POST',
        body: JSON.stringify({
          asset_type: assetType,
          prompt,
          tool: tool || null,
          quality_tier: qualityTier,
          needs_text_rendering: needsText,
          params: {},
        }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing-generation-jobs'] });
      setPrompt('');
    },
  });

  const tools: Record<string, Array<{ key: string; label: string; configured?: boolean }>> = {
    image: [
      { key: '', label: 'Auto-pick (cheapest first)' },
      { key: 'flux', label: 'Flux 1.1 Pro (~$0.04/img)' },
      { key: 'imagen', label: 'Imagen 4 Fast ($0.02/img, best for text)' },
      { key: 'gpt_image', label: 'GPT Image 1.5 ($0.04/img, premium fallback)' },
    ],
    video: [
      { key: '', label: 'Auto-pick (cheapest first)' },
      { key: 'runway', label: 'Runway Gen-4 Turbo (~$0.05-0.10/sec)' },
      { key: 'veo', label: 'Veo 3.1 ($0.15/sec, premium)' },
    ],
    voiceover: [{ key: 'elevenlabs', label: 'ElevenLabs (~$0.20/min)' }],
    presenter: [{ key: 'heygen', label: 'HeyGen ($1/min standard)' }],
  };

  const Icon = { image: ImageIcon, video: Video, voiceover: Mic, presenter: User }[assetType];

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
      <div className="lg:col-span-7 space-y-3 rounded-lg bg-white p-4" style={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.1)' }}>
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Asset type</label>
          <div className="grid grid-cols-4 gap-2">
            {(['image', 'video', 'voiceover', 'presenter'] as const).map(t => {
              const Ti = { image: ImageIcon, video: Video, voiceover: Mic, presenter: User }[t];
              return (
                <button
                  key={t}
                  onClick={() => setAssetType(t)}
                  className="flex flex-col items-center gap-1 rounded border p-3 text-xs"
                  style={
                    assetType === t
                      ? { borderColor: 'var(--cedar-primary)', backgroundColor: 'var(--cedar-subtle)', color: 'var(--cedar-primary)' }
                      : { borderColor: 'var(--border)', color: 'var(--text-secondary)' }
                  }
                >
                  <Ti className="h-5 w-5" />
                  {t}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Tool</label>
          <select value={tool} onChange={e => setTool(e.target.value)} className="w-full rounded border border-gray-200 px-2 py-1.5 text-xs">
            {tools[assetType].map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </div>

        {assetType === 'image' && (
          <>
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={needsText} onChange={e => setNeedsText(e.target.checked)} />
              Image will contain readable text (auto-routes to Imagen)
            </label>
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={qualityTier === 'premium'} onChange={e => setQualityTier(e.target.checked ? 'premium' : 'default')} />
              Premium quality tier (uses GPT Image)
            </label>
          </>
        )}

        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Prompt</label>
          <textarea
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            rows={6}
            className="w-full rounded border border-gray-200 px-2 py-1.5 text-sm font-mono"
            placeholder={
              assetType === 'image' ? 'Authentic athlete in motion, outdoor morning light, Cedar green palette...' :
              assetType === 'video' ? 'A 6-second clip of a runner cresting a hill at sunrise, slow-motion...' :
              assetType === 'voiceover' ? "Welcome back. I noticed you missed your run yesterday — let's adjust..." :
              'Describe the avatar, scene, and the script you want them to read'
            }
          />
        </div>

        <button
          onClick={() => generate.mutate()}
          disabled={!prompt.trim() || generate.isPending}
          className="flex w-full items-center justify-center gap-2 rounded px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          style={{ backgroundColor: 'var(--cedar-primary)' }}
        >
          <Wand2 className="h-4 w-4" />
          {generate.isPending ? 'Submitting...' : 'Generate'}
        </button>

        {generate.isError && (
          <div className="rounded bg-red-50 p-2 text-xs text-red-700">
            <AlertTriangle className="mr-1 inline h-3 w-3" />
            {(generate.error as Error)?.message}
          </div>
        )}

        {generate.isSuccess && (
          <ResultPanel result={(generate.data as { data: GenerationJob; note: string; is_configured: boolean })} />
        )}
      </div>

      <div className="lg:col-span-5 space-y-3 rounded-lg bg-amber-50 p-4 text-xs text-amber-900">
        <div className="text-sm font-bold">
          <AlertTriangle className="mr-1 inline h-4 w-4" />
          API key configuration
        </div>
        <p>
          Generation tools degrade gracefully when API keys aren't set up. You'll see
          <code className="rounded bg-white px-1">configure_api_key</code> error codes
          until credentials are pasted in <strong>Settings → Connections & Keys</strong>.
        </p>
        <ul className="list-disc space-y-1 pl-4">
          <li><strong>Flux</strong> → fal.ai → paste under <code>falai/api_key</code></li>
          <li><strong>Imagen / Veo</strong> → Vertex AI → needs Google Cloud service account JSON</li>
          <li><strong>GPT Image</strong> → OpenAI → paste under <code>openai/api_key</code></li>
          <li><strong>Runway</strong> → paste under <code>runway/api_key</code></li>
          <li><strong>ElevenLabs</strong> → paste under <code>elevenlabs/api_key</code></li>
          <li><strong>HeyGen</strong> → paste under <code>heygen/api_key</code></li>
        </ul>
      </div>
    </div>
  );
}

function ResultPanel({ result }: { result: { data: GenerationJob; note: string; is_configured: boolean } }) {
  const job = result.data;
  return (
    <div className="rounded-lg border-2 p-3 text-xs" style={{ borderColor: 'var(--cedar-primary)', backgroundColor: 'var(--cedar-subtle)' }}>
      <div className="font-bold" style={{ color: 'var(--cedar-primary)' }}>
        <Check className="mr-1 inline h-3 w-3" />
        Job {job.status}
      </div>
      <div className="mt-1 text-gray-700">
        Tool: <code>{job.tool}</code> · Estimated cost: ${(job.estimated_cost_cents / 100).toFixed(2)}
        {job.cost_approval_required && (
          <span className="ml-2 rounded bg-amber-200 px-1 py-0.5 text-[10px] font-bold text-amber-900">
            APPROVAL REQUIRED ({'>'} $5)
          </span>
        )}
      </div>
      {!result.is_configured && (
        <div className="mt-2 rounded bg-amber-100 p-2 text-amber-900">
          <AlertTriangle className="mr-1 inline h-3 w-3" />
          {job.error_message || 'API key not configured. Add it in Settings.'}
        </div>
      )}
      <div className="mt-1 text-[10px] italic text-gray-500">{result.note}</div>
    </div>
  );
}

function LibraryTab() {
  const { data } = useQuery<{ data: Asset[] }>({
    queryKey: ['marketing-asset-library'],
    queryFn: () => adminFetch('/api/admin/marketing/creative/asset-library'),
  });

  if (!data?.data?.length) {
    return (
      <div className="rounded-lg bg-white p-8 text-center text-sm text-gray-400" style={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.1)' }}>
        <Library className="mx-auto mb-2 h-8 w-8 text-gray-300" />
        Asset library empty. Generate or upload assets to populate.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
      {data.data.map(a => (
        <div key={a.id} className="overflow-hidden rounded-lg bg-white" style={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.1)' }}>
          {a.rendered_url ? (
            <img src={a.rendered_url} alt={a.name} className="h-32 w-full object-cover" />
          ) : (
            <div className="flex h-32 w-full items-center justify-center bg-gray-100 text-gray-400">
              <ImageIcon className="h-8 w-8" />
            </div>
          )}
          <div className="p-2">
            <div className="line-clamp-1 text-xs font-medium text-gray-900">{a.name}</div>
            <div className="text-[10px] text-gray-500">
              {a.asset_kind}{a.generation_tool && ` · ${a.generation_tool}`}
            </div>
            <div className="text-[10px] text-gray-400">${((a.generation_cost_cents || 0) / 100).toFixed(3)}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function JobsTab() {
  const { data } = useQuery<{ data: GenerationJob[] }>({
    queryKey: ['marketing-generation-jobs'],
    queryFn: () => adminFetch('/api/admin/marketing/creative/jobs'),
    refetchInterval: 10_000,
  });

  return (
    <div className="space-y-2">
      {data?.data?.map(j => (
        <div key={j.id} className="rounded-lg bg-white p-3" style={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.1)' }}>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-xs">
                <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono">{j.tool}</span>
                <span className="text-gray-500">{j.asset_type}</span>
                <span
                  className="rounded px-1.5 py-0.5 text-[10px] font-medium uppercase"
                  style={{
                    backgroundColor:
                      j.status === 'complete' ? 'var(--cedar-subtle)' :
                      j.status === 'cached' ? 'var(--status-info-bg)' :
                      j.status === 'failed' ? 'var(--status-error-bg)' :
                      'var(--status-warning-bg)',
                    color:
                      j.status === 'complete' ? 'var(--cedar-primary)' :
                      j.status === 'cached' ? 'var(--status-info-text)' :
                      j.status === 'failed' ? 'var(--status-error-text)' :
                      'var(--status-warning-text)',
                  }}
                >
                  {j.status}
                </span>
              </div>
              <div className="mt-1 line-clamp-2 text-xs text-gray-700">{j.prompt}</div>
              {j.error_message && <div className="mt-1 text-[10px] text-red-600">{j.error_message}</div>}
            </div>
            <div className="text-right text-[10px] text-gray-500">
              <div>${(j.estimated_cost_cents / 100).toFixed(2)} est</div>
              {j.actual_cost_cents !== null && <div>${((j.actual_cost_cents || 0) / 100).toFixed(2)} actual</div>}
              <div>{new Date(j.requested_at).toLocaleString()}</div>
            </div>
          </div>
        </div>
      ))}
      {!data?.data?.length && (
        <div className="rounded-lg bg-white p-8 text-center text-sm text-gray-400" style={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.1)' }}>
          No generation jobs yet.
        </div>
      )}
    </div>
  );
}

export default CreativeStudioPage;
